/**
 * Picking the day a product page should open on when the URL names no date.
 *
 * The availability calendar already expands a specific day when the page is deep
 * linked with `?startdate=`. Without one it used to open with nothing selected,
 * leaving the visitor to hunt for the first bookable day themselves. These helpers
 * choose that day instead: the soonest date with availability strictly after today.
 */

/** Calendar day keys are `YYYY-MM-DD`, matching `byDateNormalised` in the panel. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Earliest key in `availableDateKeys` that falls strictly after `afterDate`, or
 * null when none does.
 *
 * Both sides are zero-padded `YYYY-MM-DD`, so plain string comparison orders them
 * chronologically and this needs no date parsing — worth keeping in mind if the key
 * format ever changes, because the comparison would then silently misorder rather
 * than fail. Malformed keys are ignored rather than trusted to compare sensibly.
 */
export const findNextAvailableDate = (
    availableDateKeys: string[],
    afterDate: string,
): string | null => {
    if (!ISO_DAY.test(afterDate)) return null;
    let earliest: string | null = null;
    availableDateKeys.forEach((key) => {
        if (!ISO_DAY.test(key) || key <= afterDate) return;
        if (earliest === null || key < earliest) earliest = key;
    });
    return earliest;
};
